import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { Divider, ToastContext } from '@reveni-io/reveni-ui'

import ReturnProtectionInfo from '@/common/components/ReturnProtectionInfo/ReturnProtectionInfo'
import SaveChangesButtons from '@/common/components/SaveChangesButtons/SaveChangesButtons'
import UnsavedChangesAlert from '@/common/components/UnsavedChangesAlert/UnsavedChangesAlert'
import AppContext from '@/common/context/app.context'
import { focusOnError } from '@/configuration/pages/ConfigurationPage/helpers'
import RegionsSelectedNotSupportedAlert from '@/configuration/pages/ConfigurationPage/LogisticsConfiguration/components/RegionsSelectedNotSupportedAlert/RegionsSelectedNotSupportedAlert'
import routes from '@/Router/routes.constants'

import AdditionalConfigurationSection from './AdditionalConfigurationSection/AdditionalConfigurationSection'
import AvailabilitySection from './AvailabilitySection/AvailabilitySection'
import CostSection from './CostSection/CostSection'
import DeliveryMethodDetailConfigurationSkeleton from './DeliveryMethodDetailConfigurationSkeleton/DeliveryMethodDetailConfigurationSkeleton'
import DeliveryMethodTypeSection from './DeliveryMethodTypeSection/DeliveryMethodTypeSection'
import GeneralSection from './GeneralSection/GeneralSection'
import useDeliveryMethod from './hooks/useDeliveryMethod/useDeliveryMethod'
import LogisticsSection from './LogisticsSection/LogisticsSection'
import VisibilitySection from './VisibilitySection/VisibilitySection'
import {
  getAvailableLanguages,
  getDeliveryMethodPayload,
  getRegionsWithReturnProtectionDisabled,
  getReturnProtectionRegionIds,
  validateDeliveryMethod,
} from './helpers'
import * as Styles from './styles'

const DeliveryMethodDetailConfiguration = () => {
  const { id } = useParams()
  const { info, updateContext } = useContext(AppContext)
  const { setToastInfo } = useContext(ToastContext)
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()

  const supportedLanguages = info?.selectedStore?.supported_languages || ['en']
  const defaultStoreCurrency = info?.selectedStore?.currency
  const subscriptionPlan = info?.subscription_plan
  const allowsUnifiedFlow = info?.selectedStore?.preferences?.allows_unified_flow
  const storeId = info.selectedStore.id

  const {
    deliveryMethod,
    deliveryMethodType,
    regions,
    fetchingRegions,
    errors,
    currencies,
    fetchingCurrencies,
    editDeliveryMethod,
    editingDeliveryMethod,
    createDeliveryMethod,
    creatingDeliveryMethod,
    hasChanges,
    setHasChanges,
    setErrors,
    revertChanges,
    carriers,
    carrierServices,
    fetchingCarriers,
    fetchingCarrierServices,
    manageCarrierService,
    handleCarrierChange,
    handleManageCarrierServiceChange,
    handleChangeDeliveryMethodType,
    onChangeDeliveryMethod,
    handleChangeInternalName,
  } = useDeliveryMethod({
    id,
    selectedDeliveryMethod: location.state?.selectedDeliveryMethod,
    supportedLanguages,
    storeId,
    subscriptionPlan,
  })

  const isCoveredByReturnProtection = deliveryMethod?.is_covered_by_return_protection
  const isCreating = id === 'new' || id?.includes('duplicate-')
  const isTypeLocked = !!deliveryMethod?.id

  const returnProtectionRegionIds = isCreating
    ? getReturnProtectionRegionIds(info?.selectedStore?.return_protection)
    : []

  const availableRegions = getRegionsWithReturnProtectionDisabled({
    regions,
    returnProtectionRegionIds,
    reason: t('Reveni gestiona este país a través de Return Protection'),
  })

  const onSave = async () => {
    const validationErrors = validateDeliveryMethod({
      deliveryMethod,
      supportedLanguages,
      regions,
    })

    if (validationErrors.length) {
      focusOnError({ [validationErrors[0].type]: validationErrors[0].type })
      setErrors(validationErrors)
      return
    }

    const payload = getDeliveryMethodPayload(deliveryMethod, defaultStoreCurrency, manageCarrierService)

    try {
      if (isCreating) {
        await createDeliveryMethod({ storeId, deliveryMethod: payload })
        updateContext(context => ({ ...context, has_delivery_methods: true }))
      } else {
        await editDeliveryMethod({ storeId, deliveryMethod: payload })
        setHasChanges(false)
      }
      setToastInfo({ show: true, message: t('Cambios guardados'), type: 'success' })
      navigate(routes.configurationPage.logistics.deliveryMethods)
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  if (fetchingRegions || fetchingCurrencies) {
    return <DeliveryMethodDetailConfigurationSkeleton allowsUnifiedFlow={allowsUnifiedFlow} />
  }

  return (
    <Styles.MainContainer>
      {hasChanges && <UnsavedChangesAlert />}
      {isCoveredByReturnProtection && (
        <ReturnProtectionInfo
          message={t(
            'Este método de devolución está cubierto por tu contrato de Return Protection. Reveni gestiona su disponibilidad, coste y logística, por lo que estos campos no se pueden editar.'
          )}
        />
      )}
      <GeneralSection
        internalName={deliveryMethod?.internal_name}
        names={deliveryMethod?.name}
        languages={getAvailableLanguages(supportedLanguages)}
        errors={errors}
        onChangeInternalName={handleChangeInternalName}
        onChange={onChangeDeliveryMethod}
      />
      <Divider />
      <DeliveryMethodTypeSection
        value={deliveryMethodType}
        onChange={handleChangeDeliveryMethodType}
        disabled={isTypeLocked}
        showReadOnlyMessage={isTypeLocked && !isCreating}
      />
      <Divider />
      <AvailabilitySection
        config={deliveryMethod}
        regions={availableRegions}
        errors={errors}
        onChange={onChangeDeliveryMethod}
        disabled={isCoveredByReturnProtection}
        allowsUnifiedFlow={allowsUnifiedFlow}
        storeId={storeId}
      />
      <Divider />
      <LogisticsSection
        deliveryMethodType={deliveryMethodType}
        config={deliveryMethod}
        onChange={onChangeDeliveryMethod}
        errors={errors}
        subscriptionPlan={subscriptionPlan}
        carriers={carriers}
        carrierServices={carrierServices}
        onCarrierChange={handleCarrierChange}
        manageCarrierService={manageCarrierService}
        onManageCarrierServiceChange={handleManageCarrierServiceChange}
        isLoadingCarrierServices={fetchingCarrierServices || fetchingCarriers}
        disabled={isCoveredByReturnProtection}
      />
      <Divider />
      <CostSection
        key={deliveryMethodType}
        config={deliveryMethod}
        currencies={currencies}
        defaultStoreCurrency={defaultStoreCurrency}
        onChange={onChangeDeliveryMethod}
        disabled={isCoveredByReturnProtection}
        deliveryMethodType={deliveryMethodType}
      />
      <Divider />
      <VisibilitySection
        config={deliveryMethod}
        onChange={onChangeDeliveryMethod}
        disabled={isCoveredByReturnProtection}
        deliveryMethodType={deliveryMethodType}
        storeId={storeId}
      />
      <AdditionalConfigurationSection
        config={deliveryMethod}
        onChange={onChangeDeliveryMethod}
        deliveryMethodType={deliveryMethodType}
        subscriptionPlan={subscriptionPlan}
        defaultStoreCurrency={defaultStoreCurrency}
      />
      <RegionsSelectedNotSupportedAlert errors={errors} />
      <SaveChangesButtons
        loading={editingDeliveryMethod || creatingDeliveryMethod}
        disabled={editingDeliveryMethod || creatingDeliveryMethod}
        onSave={onSave}
        hasChanges={hasChanges}
        onRevertChanges={revertChanges}
      />
    </Styles.MainContainer>
  )
}

export default DeliveryMethodDetailConfiguration
